import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { AppDispatch, RootState } from '../../store';
import { paymentEndpoints } from '../../services/api/endpoints/payment.endpoint';
import {
  fetchPaymentSettings,
  syncAccountStatus,
  fetchInvoices,
  fetchInvoiceById,
  fetchInvoiceByBooking,
  fetchWalletBalance,
  fetchWalletTransactions,
  fetchPayouts,
  fetchRefunds,
  selectPayment,
  setSelectedInvoice,
  clearErrors,
  resetPaymentState,
  clearInvoices,
} from '../../store/slices/paymentSlice';
import type {
  CreateInvoiceRequest,
  CreateRefundRequest,
  WithdrawalRequest,
  ListInvoicesParams,
  ListTransactionsParams,
  ListPayoutsParams,
  ListRefundsParams,
  Invoice,
} from '../../types/payment';
import { logger } from '../../utils/logger';

/**
 * Custom hook for payment operations
 * Provides state and action functions for all payment features
 */
export const usePayments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const paymentState = useSelector(selectPayment);
  const selectedOrganisation = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const organizationId = selectedOrganisation?.id;

  // ==================== ONBOARDING ====================

  const startOnboarding = useCallback(
    async (billingEmail: string) => {
      if (!organizationId) throw new Error('No organization selected');

      const currentUrl = window.location.origin;
      const result = await paymentEndpoints.startOnboarding(organizationId, {
        billingEmail,
        refreshUrl: `${currentUrl}/dashboard/payment/setup`,
        returnUrl: `${currentUrl}/dashboard/payment/setup`,
      });

      // Redirect to Stripe onboarding in the same window
      logger.info('Redirecting to Stripe onboarding', { organizationId });
      window.location.href = result.data.url;

      return result;
    },
    [organizationId]
  );

  const refreshSettings = useCallback(() => {
    if (organizationId) {
      dispatch(fetchPaymentSettings(organizationId));
    }
  }, [organizationId, dispatch]);

  const syncStripeAccount = useCallback(async () => {
    if (organizationId) {
      await dispatch(syncAccountStatus(organizationId)).unwrap();
    }
  }, [organizationId, dispatch]);

  const updateSettings = useCallback(
    async (updates: Partial<any>) => {
      if (!organizationId) throw new Error('No organization selected');

      const result = await paymentEndpoints.updatePaymentSettings(organizationId, updates);
      refreshSettings();
      return result;
    },
    [organizationId, refreshSettings]
  );

  // ==================== INVOICES ====================

  const refreshInvoices = useCallback(
    (params?: ListInvoicesParams) => {
      if (organizationId) {
        dispatch(fetchInvoices({ organizationId, params }));
      }
    },
    [organizationId, dispatch]
  );

  const getInvoice = useCallback(
    (invoiceId: string) => {
      if (organizationId) {
        dispatch(fetchInvoiceById({ organizationId, invoiceId }));
      }
    },
    [organizationId, dispatch]
  );

  const getInvoiceForBooking = useCallback(
    (bookingId: string) => {
      if (organizationId) {
        dispatch(fetchInvoiceByBooking({ organizationId, bookingId }));
      }
    },
    [organizationId, dispatch]
  );

  const createInvoice = useCallback(
    async (data: CreateInvoiceRequest) => {
      if (!organizationId) throw new Error('No organization selected');

      const result = await paymentEndpoints.createInvoice(organizationId, data);
      refreshInvoices();
      return result;
    },
    [organizationId, refreshInvoices]
  );

  const createPaymentSession = useCallback(
    async (invoiceId: string) => {
      if (!organizationId) throw new Error('No organization selected');

      const result = await paymentEndpoints.createPaymentSession(organizationId, invoiceId);
      return result.data;
    },
    [organizationId]
  );

  const payInvoice = useCallback(
    async (invoiceId: string) => {
      const session = await createPaymentSession(invoiceId);
      // Open payment in a new tab
      window.open(session.checkoutUrl, '_blank');
    },
    [createPaymentSession]
  );

  const sendInvoice = useCallback(
    async (invoiceId: string) => {
      if (!organizationId) throw new Error('No organization selected');

      await paymentEndpoints.sendInvoice(organizationId, invoiceId);
      logger.info('Invoice sent successfully', { invoiceId });
    },
    [organizationId]
  );

  const resendInvoice = useCallback(
    async (invoice: Invoice) => {
      if (!organizationId) throw new Error('No organization selected');

      // First get a checkout URL by creating a payment session
      const session = await paymentEndpoints.createPaymentSession(
        organizationId,
        invoice.id.toString()
      );

      const payload = {
        orgId: invoice.orgId,
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        customerId: invoice.customerId,
        checkoutUrl: session.data.checkoutUrl,
        totalCents: invoice.totalCents,
        currency: invoice.currency,
        email: invoice.customer?.email || '',
        customerName: invoice.customer
          ? `${invoice.customer.firstName} ${invoice.customer.lastName || ''}`.trim()
          : '',
        phone: invoice.customer?.phone || '',
      };

      await paymentEndpoints.resendInvoice(organizationId, payload);
      logger.info('Invoice resent successfully', { invoiceId: invoice.id });
    },
    [organizationId]
  );

  const downloadInvoice = useCallback(
    async (invoice: Invoice) => {
      if (!organizationId) throw new Error('No organization selected');

      const response = await paymentEndpoints.downloadInvoice(
        organizationId,
        invoice.id.toString()
      );

      // Open PDF in new tab (browser will download or display it)
      window.open(response.data.urls.pdf, '_blank');

      logger.info('Invoice download opened', { invoiceId: invoice.id });
    },
    [organizationId]
  );

  // ==================== WALLET ====================

  const refreshBalance = useCallback(() => {
    if (organizationId) {
      dispatch(fetchWalletBalance(organizationId));
    }
  }, [organizationId, dispatch]);

  const refreshTransactions = useCallback(
    (params?: ListTransactionsParams) => {
      if (organizationId) {
        dispatch(fetchWalletTransactions({ organizationId, params }));
      }
    },
    [organizationId, dispatch]
  );

  const refreshPayouts = useCallback(
    (params?: ListPayoutsParams) => {
      if (organizationId) {
        dispatch(fetchPayouts({ organizationId, params }));
      }
    },
    [organizationId, dispatch]
  );

  const requestWithdrawal = useCallback(
    async (amountCents: number, statementDescriptor?: string) => {
      if (!organizationId) throw new Error('No organization selected');

      const result = await paymentEndpoints.requestWithdrawal(organizationId, {
        amountCents,
        statementDescriptor,
      });

      refreshBalance();
      refreshTransactions();
      refreshPayouts();

      logger.info('Withdrawal requested successfully', { amountCents });
      return result;
    },
    [organizationId, refreshBalance, refreshTransactions, refreshPayouts]
  );

  // ==================== REFUNDS ====================

  const refreshRefunds = useCallback(
    (params?: ListRefundsParams) => {
      if (organizationId) {
        dispatch(fetchRefunds({ organizationId, params }));
      }
    },
    [organizationId, dispatch]
  );

  const requestRefund = useCallback(
    async (invoiceId: string, data: CreateRefundRequest) => {
      if (!organizationId) throw new Error('No organization selected');

      const result = await paymentEndpoints.createRefund(organizationId, invoiceId, data);

      refreshInvoices();
      refreshRefunds();

      logger.info('Refund requested successfully', { invoiceId, amount: data.amountCents });
      return result;
    },
    [organizationId, refreshInvoices, refreshRefunds]
  );

  // ==================== UTILITY FUNCTIONS ====================

  const selectInvoice = useCallback(
    (invoiceId: string) => {
      const invoice = paymentState.invoices.find(inv => inv.id.toString() === invoiceId);
      if (invoice) {
        dispatch(setSelectedInvoice(invoice));
      } else {
        getInvoice(invoiceId);
      }
    },
    [paymentState.invoices, dispatch, getInvoice]
  );

  const clearSelectedInvoice = useCallback(() => {
    dispatch(setSelectedInvoice(null));
  }, [dispatch]);

  const clearAllErrors = useCallback(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  const resetState = useCallback(() => {
    dispatch(resetPaymentState());
  }, [dispatch]);

  const clearInvoicesList = useCallback(() => {
    dispatch(clearInvoices());
  }, [dispatch]);

  // ==================== RETURN ====================

  return {
    // State
    ...paymentState,
    selectedOrganizationId: organizationId,

    // Onboarding
    startOnboarding,
    refreshSettings,
    syncStripeAccount,
    updateSettings,

    // Invoices
    refreshInvoices,
    getInvoice,
    getInvoiceForBooking,
    createInvoice,
    createPaymentSession,
    payInvoice,
    sendInvoice,
    resendInvoice,
    downloadInvoice,
    selectInvoice,
    clearSelectedInvoice,
    clearInvoicesList,

    // Wallet
    refreshBalance,
    refreshTransactions,
    refreshPayouts,
    requestWithdrawal,

    // Refunds
    refreshRefunds,
    requestRefund,

    // Utilities
    clearAllErrors,
    resetState,
  };
};
